import { Hide } from '@iheartjane/reefer';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { EmailEntry } from './emailEntry/emailEntry';
import type { EmailEntryFormData } from './emailEntry/emailEntry';
import { ForgotPassword } from './forgotPassword/forgotPassword';
import type { EmailFormData } from './forgotPassword/forgotPassword';
import { SignUp } from './signUp/signUp';
import type { SignUpFormData } from './signUp/signUp';
import { WelcomeBack } from './welcomeBack/welcomeBack';
import type { WelcomeBackFormData } from './welcomeBack/welcomeBack';

export type ScreenName =
  | 'email-entry'
  | 'welcome-back'
  | 'sign-up'
  | 'forgot-password';

const screenLabels: Record<ScreenName, string> = {
  'email-entry': 'Email entry form',
  'welcome-back': 'Welcome back form',
  'sign-up': 'Create account form',
  'forgot-password': 'Reset password form',
};

interface AuthenticationErrors {
  emailCheck?: string[];
  resetPassword?: string[];
  signIn?: string[];
  signUp?: string[];
}

export interface SignInData {
  email: string;
  password: string;
}

export interface AuthenticationProps {
  /** Optional pre-filled email shown on the Email Entry screen. */
  defaultEmail?: string;

  /** Errors from the various authentication steps. */
  errors: AuthenticationErrors | null;

  /**
   * Function called to check whether an account exists for the given email.
   * Returning `true` advances to the Welcome Back screen; `false` advances to Sign Up.
   * Throw to keep the user on the email-entry screen and surface the thrown
   * message inline.
   */
  onCheckUserExists(email: string): Promise<boolean>;

  /** Function called when the modal should close (Cancel buttons). */
  onClose(): void;

  /** Function called when the email is submitted to Forgot Password. Returns `true` on success. */
  onForgotPasswordSubmit(data: EmailFormData): Promise<boolean>;

  /** Function called when an existing user submits their password on the Welcome Back screen. */
  onSignInSubmit(data: SignInData): void;

  /** Function called when a new user submits the Sign Up form. */
  onSignUpSubmit(data: SignUpFormData): void;

  /**
   * Whether to render the marketing promotions opt-in checkbox on Sign Up.
   * Marketplace-specific behavior; embedded menus should leave this off.
   */
  showPromotionsOptIn?: boolean;

  /** Optional React node displayed at the bottom of the Email Entry / Welcome Back screens. */
  signInFooter?: ReactNode;

  /**
   * Optional React node(s) rendered as SSO buttons on the Email Entry screen
   * (e.g. `<GoogleButton onClick={...} />`, `<AppleButton onClick={...} />`).
   */
  ssoButtons?: ReactNode;
}

export const Authentication = ({
  defaultEmail,
  errors,
  onCheckUserExists,
  onClose,
  onForgotPasswordSubmit,
  onSignInSubmit,
  onSignUpSubmit,
  showPromotionsOptIn,
  signInFooter,
  ssoButtons,
}: AuthenticationProps) => {
  const [screen, setScreen] = useState<ScreenName>('email-entry');
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [emailCheckError, setEmailCheckError] = useState<string | null>(null);
  const [hasResetPassword, setHasResetPassword] = useState(false);

  const handleEmailSubmit = async ({
    email: submittedEmail,
  }: EmailEntryFormData) => {
    setEmail(submittedEmail);
    setEmailCheckError(null);
    try {
      const exists = await onCheckUserExists(submittedEmail);
      setScreen(exists ? 'welcome-back' : 'sign-up');
    } catch (err) {
      setEmailCheckError(
        err instanceof Error && err.message
          ? err.message
          : 'We could not check that email. Please try again.'
      );
    }
  };

  const handleWelcomeBackSubmit = ({ password }: WelcomeBackFormData) => {
    onSignInSubmit({ email, password });
  };

  const handleForgotPassword = async (data: EmailFormData) => {
    const success = await onForgotPasswordSubmit(data);
    if (success) {
      setHasResetPassword(true);
      setScreen('welcome-back');
    }
    return success;
  };

  return (
    <>
      <Hide isHidden aria-live="polite" id="auth-screen-label">
        {screenLabels[screen]}
      </Hide>

      {screen === 'email-entry' && (
        <EmailEntry
          defaultEmail={email}
          errors={[
            ...(errors?.emailCheck ?? []),
            ...(emailCheckError ? [emailCheckError] : []),
          ]}
          onCancel={onClose}
          onSubmit={handleEmailSubmit}
          signInFooter={signInFooter}
          ssoButtons={ssoButtons}
        />
      )}

      {screen === 'welcome-back' && (
        <WelcomeBack
          email={email}
          errors={errors?.signIn ?? null}
          notice={
            hasResetPassword
              ? 'Check your email for password reset instructions.'
              : null
          }
          onCancel={() => {
            setHasResetPassword(false);
            setScreen('email-entry');
          }}
          onEditEmail={() => {
            setHasResetPassword(false);
            setScreen('email-entry');
          }}
          onForgotPassword={() => setScreen('forgot-password')}
          onSubmit={handleWelcomeBackSubmit}
          signInFooter={signInFooter}
        />
      )}

      {screen === 'sign-up' && (
        <SignUp
          defaultEmail={email}
          errors={errors?.signUp || null}
          onCancel={() => setScreen('email-entry')}
          onSubmit={onSignUpSubmit}
          showPromotionsOptIn={showPromotionsOptIn}
        />
      )}

      {screen === 'forgot-password' && (
        <ForgotPassword
          errors={errors?.resetPassword || null}
          onCancel={() => setScreen('welcome-back')}
          onSubmit={handleForgotPassword}
        />
      )}
    </>
  );
};
